import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

export interface SavedFilterDialogValidationMessages {
  required?: string;
  maxlength?: string;
}

export interface SavedFilterDialogData {
  title?: string;
  nameLabel?: string;
  saveButtonLabel?: string;
  cancelButtonLabel?: string;
  snackbarActionLabel?: string;
  snackbarDuration?: number;
  nameMaxLength?: number;
  flagControlKey?: string;
  nameControlKey?: string;
  showFlagToggle?: boolean;
  flagToggleLabel?: string;
  initialValues?: Record<string, unknown>;
  validationMessages?: SavedFilterDialogValidationMessages;
}

export interface SavedFilterDialogResult {
  name: string;
  [key: string]: unknown;
}

const DEFAULT_VALIDATION_MESSAGES: Required<SavedFilterDialogValidationMessages> = {
  required: 'Please choose a name for this filter.',
  maxlength: 'The name you have chosen is too long.',
};

const DEFAULT_CONFIG: Required<Omit<SavedFilterDialogData, 'initialValues' | 'validationMessages'>> &
  Pick<SavedFilterDialogData, 'initialValues' | 'validationMessages'> = {
  title: 'Save Filters',
  nameLabel: 'Name',
  saveButtonLabel: 'Save',
  cancelButtonLabel: 'Cancel',
  snackbarActionLabel: 'Close',
  snackbarDuration: 5000,
  nameMaxLength: 70,
  flagControlKey: 'saveFilters',
  nameControlKey: 'name',
  showFlagToggle: false,
  flagToggleLabel: 'Save filter',
  initialValues: {},
  validationMessages: undefined,
};

@Component({
  selector: 'app-saved-filter-dialog',
  templateUrl: './saved-filter-dialog.component.html',
  styleUrls: ['./saved-filter-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SavedFilterDialogComponent implements OnInit {
  form: UntypedFormGroup;
  config: Required<Omit<SavedFilterDialogData, 'validationMessages'>> & {
    validationMessages: Required<SavedFilterDialogValidationMessages>;
  };
  nameControlKey: string;
  flagControlKey: string;

  constructor(
    private readonly dialogRef: MatDialogRef<SavedFilterDialogComponent, SavedFilterDialogResult>,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) data: SavedFilterDialogData | null
  ) {
    const config = { ...DEFAULT_CONFIG, ...(data ?? {}) };
    const validationMessages = {
      ...DEFAULT_VALIDATION_MESSAGES,
      ...(data?.validationMessages ?? {}),
    };

    this.config = {
      ...config,
      validationMessages,
      initialValues: { ...(config.initialValues ?? {}) },
    };

    this.nameControlKey = this.config.nameControlKey;
    this.flagControlKey = this.config.flagControlKey;
  }

  ngOnInit(): void {
    this.buildForm();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.form.invalid) {
      this.showValidationMessage();
      return;
    }

    const rawValue = this.form.getRawValue() as SavedFilterDialogResult;
    this.dialogRef.close(rawValue);
  }

  get isNameVisible(): boolean {
    if (!this.form || !this.config.showFlagToggle) {
      return true;
    }

    const flagControl = this.form.get(this.flagControlKey);
    return flagControl?.value !== false;
  }

  private buildForm(): void {
    const controls: Record<string, unknown> = {};
    controls[this.flagControlKey] = [this.config.initialValues?.[this.flagControlKey] ?? true];
    controls[this.nameControlKey] = [
      this.config.initialValues?.[this.nameControlKey] ?? null,
      [Validators.required, Validators.maxLength(this.config.nameMaxLength)],
    ];

    this.form = this.formBuilder.group(controls);
  }

  private showValidationMessage(): void {
    const nameControl = this.form.get(this.nameControlKey);
    const errors = nameControl?.errors;

    if (!errors) {
      return;
    }

    let message: string | undefined;

    if (errors['required']) {
      message = this.config.validationMessages.required;
    } else if (errors['maxlength']) {
      message = this.config.validationMessages.maxlength;
    }

    if (message) {
      this.snackBar.open(message, this.config.snackbarActionLabel, {
        duration: this.config.snackbarDuration,
      });
    }
  }
}
